import { fontFamily } from '@design-system/tokens';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enUS from 'date-fns/locale/en-US';

import { sprinkles } from '@design-system/sprinkles.css';
import {
  DatePicker,
  DateValidationError,
  LocalizationProvider,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { Col } from '@design-system/components/grid/grid';
import { ErrorMessage } from '@client/new/components/Label';
import { GlobalStyles } from '@mui/material';
import { forwardRef } from 'react';

const GlobalDateInputStyles = () => (
  <GlobalStyles
    styles={{
      '.Mui-error': {
        fontFamily: fontFamily.visuelt,
        boxShadow: 'none !important',
      },
    }}
  />
);

interface DateInputProps {
  value: unknown;
  onChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
  error?: string;
  width?: number;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ name, error, width, ...p }, ref) => {
    const handleDateChange = (
      value: Date | null,
      context: PickerChangeHandlerContext<DateValidationError>,
    ) => {
      if (!context.validationError && value) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        p.onChange({ target: { value: value.getTime(), name } } as any);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        p.onChange({ target: { value: undefined, name } } as any);
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <GlobalDateInputStyles />
        <Col
          style={{
            marginTop: '1px',
            ...(width ? { width: `${width}px` } : {}),
          }}
        >
          <DatePicker
            views={['year', 'month', 'day']}
            openTo="day"
            value={typeof p.value === 'number' ? new Date(p.value) : undefined}
            onChange={handleDateChange}
            disablePast
            sx={{
              boxShadow: 'none !important',
              fontFamily: fontFamily.visuelt,
              '>div': {
                fontFamily: fontFamily.visuelt,
                height: '44px',
                borderWidth: '2px',
                borderRadius: '10px',
                borderColor: '#e4e4e4',
              },
            }}
            slotProps={{
              popper: {
                sx: {
                  '& .MuiPickersMonth-monthButton': {
                    fontFamily: fontFamily.visuelt,
                  },
                  '& .MuiPickersYear-yearButton': {
                    fontFamily: fontFamily.visuelt,
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    fontFamily: fontFamily.visuelt,
                  },
                },
              },
              textField: {
                inputRef: ref,
                InputProps: { sx: { fontFamily: fontFamily.visuelt } },
                error: !!error,
              },
            }}
            className={sprinkles({ fontFamily: 'visuelt' })}
          />
          <ErrorMessage content={error} />
        </Col>
      </LocalizationProvider>
    );
  },
);

DateInput.displayName = 'DateInput';
